import { Controller } from "@hotwired/stimulus"
import { phoneComplete, formattedPhone, isImageFile, acceptedPhotoTypes, PHOTO_CROP_SLOT } from "../lib/thank_you_preview_fields"

// Live thank-you page preview on the realtime order form (#14347): the shared
// card (shared/_thank_you_page_preview) mirrors the "What the Lead Sees"
// inputs on every keystroke, a chosen photo replaces the avatar, and each
// blank REQUIRED field (photo, title, phone, NPN) is highlighted with its
// "Add your …" fix link — the strongest nudge short of a gate (owner
// 2026-09-25: never a requirement).
//
// Self-contained on purpose: the card sits INSIDE the realtime-order
// controller's section but reads the inputs by id (inputsValue) and listens to
// them itself, so it needs no target on that controller and no edit to the
// field markup. Values set by script (My info / Custom / Apply Defaults) do not
// fire `input`, so the source radios and the apply button are watched too and
// the card re-reads on the next frame.
//
// Wire: data-controller="thank-you-preview"
//       data-thank-you-preview-inputs-value='{"title":"lead_order_display_agent_title", …, "photo":"lead_order_photo"}'
//       data-thank-you-preview-has-photo-value="true|false"   (a Display Photo already shows)
//       card targets: previewPhoto, previewName, previewTitle, previewPhone,
//                     previewNpnRow, previewNpn, previewCalendarRow, fixes, fix
export default class extends Controller {
  static targets = ["previewPhoto", "previewName", "previewTitle", "previewPhone",
    "previewNpnRow", "previewNpn", "previewCalendarRow", "fixes", "fix"]
  static values = { inputs: Object, hasPhoto: Boolean }

  static EMPTY_CLASS = "gl-ty-preview__empty"
  static MISSING_CLASS = "gl-ty-preview__missing"
  static PLACEHOLDERS = { name: "Your name", title: "Your title", phone: "Your phone number" }
  static REQUIRED = ["photo", "title", "phone", "npn"]
  static SOURCE_RADIOS = 'input[name="lead_order[thank_you_source]"]'
  static APPLY_DEFAULTS = '[data-action~="realtime-order#applyProfileDefaults"]'

  connect() {
    this.photoSelected = false
    this.previewSession = 0
    this.initialPreviewPhotoHTML = this.hasPreviewPhotoTarget ? this.previewPhotoTarget.innerHTML : ""
    this.listeners = []
    this.bindInputs()
    this.refresh()
  }

  disconnect() {
    this.listeners.forEach(([element, event, handler]) => element.removeEventListener(event, handler))
    this.listeners = []
  }

  refresh() {
    this.renderText("name")
    this.renderText("title")
    this.renderPhone()
    this.renderNpn()
    if (this.hasPreviewCalendarRowTarget) this.previewCalendarRowTarget.hidden = this.value("calendar").length === 0
    this.renderFixes()
  }

  // ── Wiring ────────────────────────────────────────────────────────
  bindInputs() {
    Object.entries(this.inputsValue).forEach(([field, id]) => {
      const input = document.getElementById(id)
      if (!input) return
      if (field === "photo") {
        this.listen(input, "change", (event) => this.photoChanged(event))
      } else {
        this.listen(input, "input", () => this.refresh())
        this.listen(input, "change", () => this.refresh())
      }
    })
    // Script-driven fills (My info / Custom / Apply Defaults) do not fire
    // `input` on the fields — re-read after the other controller has written.
    document.querySelectorAll(this.constructor.SOURCE_RADIOS).forEach((radio) => {
      this.listen(radio, "change", () => this.deferRefresh())
    })
    document.querySelectorAll(this.constructor.APPLY_DEFAULTS).forEach((button) => {
      this.listen(button, "click", () => this.deferRefresh())
    })
  }

  listen(element, event, handler) {
    element.addEventListener(event, handler)
    this.listeners.push([element, event, handler])
  }

  deferRefresh() {
    window.requestAnimationFrame(() => this.refresh())
  }

  // ── Rendering ──────────────────────────────────────────────────────
  renderText(field) {
    const targetName = `preview${field[0].toUpperCase()}${field.slice(1)}`
    if (!this[`has${targetName[0].toUpperCase()}${targetName.slice(1)}Target`]) return
    const element = this[`${targetName}Target`]
    const value = this.value(field)
    element.textContent = value || this.constructor.PLACEHOLDERS[field]
    element.classList.toggle(this.constructor.EMPTY_CLASS, !value)
    this.markMissing(element, field)
  }

  renderPhone() {
    if (!this.hasPreviewPhoneTarget) return
    const value = formattedPhone(this.value("phone"))
    this.previewPhoneTarget.textContent = value || this.constructor.PLACEHOLDERS.phone
    this.previewPhoneTarget.classList.toggle(this.constructor.EMPTY_CLASS, !value)
    // A partial number echoes what was typed but is still MISSING — the same
    // verdict the fix strip gives it, so the highlight and the link agree.
    this.markMissing(this.previewPhoneTarget, "phone")
  }

  renderNpn() {
    const npn = this.value("npn")
    if (this.hasPreviewNpnTarget) this.previewNpnTarget.textContent = npn
    if (this.hasPreviewNpnRowTarget) this.previewNpnRowTarget.hidden = npn.length === 0
  }

  // Only REQUIRED fields wear the missing tone, and only when a fix link
  // exists for them (a surface without links shows the plain placeholder).
  markMissing(element, field) {
    element.classList.toggle(this.constructor.MISSING_CLASS, this.fixableMissing(field))
  }

  // The strip mirrors the Ruby partial (`fix_links.keys & missing`): it shows
  // only when a MISSING field also has a rendered link. A buy-for owner's
  // card links the photo alone, so a missing title there must not open an
  // empty strip.
  renderFixes() {
    if (!this.hasFixesTarget) return
    const missing = this.missingFields()
    this.fixTargets.forEach((link) => { link.hidden = !missing.includes(link.dataset.field) })
    this.fixesTarget.hidden = !missing.some((field) => this.fixFor(field) !== null)
    if (this.hasPreviewPhotoTarget) this.markMissing(this.previewPhotoTarget, "photo")
  }

  // ONE verdict per field, read by the highlight and by the strip alike.
  fieldMissing(field) {
    if (field === "photo") return !this.photoPresent()
    if (field === "phone") return !phoneComplete(this.value("phone"))
    return this.value(field).length === 0
  }

  fixableMissing(field) {
    return this.constructor.REQUIRED.includes(field) && this.fieldMissing(field) && this.fixFor(field) !== null
  }

  missingFields() {
    return this.constructor.REQUIRED.filter((field) => this.fieldMissing(field))
  }

  fixFor(field) {
    return this.fixTargets.find((link) => link.dataset.field === field) || null
  }

  // ── Photo ──────────────────────────────────────────────────────────
  photoChanged(event) {
    const file = event.target.files?.[0]
    // Only a type the slot accepts counts (the step's rule): photo-crop refuses
    // a GIF/WebP with its own error and clears the input without re-firing
    // change, so any-image/* here would paint a photo that never attaches and
    // clear the fix link for it.
    this.photoSelected = isImageFile(file, this.slotAcceptedTypes(event.target))
    this.previewSession += 1
    if (this.photoSelected) {
      this.showPreviewPhoto(file)
    } else if (this.hasPreviewPhotoTarget) {
      this.previewPhotoTarget.innerHTML = this.initialPreviewPhotoHTML
    }
    this.renderFixes()
  }

  photoPresent() {
    return this.hasPhotoValue || this.photoSelected
  }

  // The slot's list, walked UP from the photo input — this card does not wrap
  // the slot (it reads the input by id) — or photo-crop's default when the
  // input is not inside one.
  slotAcceptedTypes(input) {
    return acceptedPhotoTypes(input?.closest(PHOTO_CROP_SLOT))
  }

  showPreviewPhoto(file) {
    if (!this.hasPreviewPhotoTarget) return
    const session = this.previewSession
    const reader = new FileReader()
    reader.onload = (e) => {
      if (session !== this.previewSession) return
      const img = document.createElement("img")
      img.src = e.target.result
      img.alt = "Your photo"
      img.className = "gl-ty-preview__img"
      this.previewPhotoTarget.replaceChildren(img)
    }
    reader.readAsDataURL(file)
  }

  // ── Input readers ──────────────────────────────────────────────────
  value(field) {
    const input = document.getElementById(this.inputsValue[field] || "")
    return input ? input.value.trim() : ""
  }
}
